/**
 * FaqSection — Morgan Legal Group
 *
 * Carries the production homepage's "Questions And Answers" block: 14 questions,
 * each opening into the full long-form guide that lives behind it on WordPress.
 * That accordion is 17,741 of the old homepage's 18,841 words and is what earns
 * the homepage its geo+practice rankings, so the content is ported verbatim
 * (see src/data/homeQa.ts) rather than summarised.
 *
 * Two rules this component must not break:
 *  1. Every panel stays in the DOM — collapsing is CSS only. Conditional
 *     rendering would strip 17,741 words out of the prerendered HTML.
 *  2. Heading levels inside a panel mirror the source page (h2/h3/h4).
 */
import { useEffect, useRef, useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { homeQa, type QaBlock } from '@/data/homeQa';

export const faqs = homeQa;

/** Consecutive `li` blocks are one list; everything else renders in place. */
function renderBlocks(blocks: QaBlock[]) {
  const out: React.ReactNode[] = [];
  let list: string[] = [];

  const flushList = (key: string) => {
    if (!list.length) return;
    out.push(
      <ul key={key} className="list-disc pl-5 space-y-2 my-4">
        {list.map((text, i) => (
          <li key={i} className="font-body text-navy/70 text-sm lg:text-base leading-relaxed">
            {text}
          </li>
        ))}
      </ul>,
    );
    list = [];
  };

  blocks.forEach((block, i) => {
    if (block.tag === 'li') {
      list.push(block.text);
      return;
    }
    flushList(`ul-${i}`);
    if (block.tag === 'h2') {
      out.push(
        <h2 key={i} className="font-display text-navy text-xl lg:text-2xl font-bold leading-snug mt-8 mb-3 first:mt-0">
          {block.text}
        </h2>,
      );
    } else if (block.tag === 'h3') {
      out.push(
        <h3 key={i} className="font-display text-navy text-lg lg:text-xl font-semibold leading-snug mt-6 mb-2">
          {block.text}
        </h3>,
      );
    } else if (block.tag === 'h4') {
      out.push(
        <h4 key={i} className="font-display text-navy/90 text-base lg:text-lg font-semibold leading-snug mt-5 mb-2">
          {block.text}
        </h4>,
      );
    } else {
      out.push(
        <p key={i} className="font-body text-navy/70 text-sm lg:text-base leading-relaxed mb-4">
          {block.text}
        </p>,
      );
    }
  });
  flushList('ul-last');
  return out;
}

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="faq" className="py-20 lg:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left — sticky header */}
          <div className="lg:col-span-4">
            <div
              className={`lg:sticky lg:top-28 transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <div className="section-label mb-4">FAQ</div>
              <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-4xl font-bold leading-tight mb-5">
                Questions And <span className="text-gold italic">Answers</span>
              </h2>
              <p className="font-body text-navy/65 text-base leading-relaxed mb-8">
                In-depth answers on estate planning, wills, trusts, and probate in New York — the same
                guidance our attorneys give in consultation. Still have questions? We&apos;re here to help.
              </p>
              <button
                onClick={() => {
                  const el = document.querySelector('#consultation');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-navy"
              >
                Ask Us Directly
              </button>

              {/* Quick links */}
              <div className="mt-8 p-5 bg-navy text-white">
                <p className="font-display text-base font-bold mb-3">Need Immediate Help?</p>
                <a
                  href="tel:8885291315"
                  className="flex items-center gap-2 text-gold font-body text-sm font-medium hover:text-gold-light transition-colors"
                >
                  📞 888-529-1315
                </a>
                <a
                  href="mailto:contact@morganlegalny.com"
                  className="flex items-center gap-2 text-white/70 font-body text-sm mt-2 hover:text-gold transition-colors"
                >
                  ✉ contact@morganlegalny.com
                </a>
              </div>
            </div>
          </div>

          {/* Right — accordion */}
          <div className="lg:col-span-8">
            <div className="space-y-2">
              {homeQa.map((faq, i) => (
                <div
                  key={i}
                  className={`bg-white border transition-all duration-300 ${
                    openIndex === i ? 'border-gold/50 shadow-md' : 'border-gold/15 hover:border-gold/30'
                  } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <button
                    className="w-full flex items-start justify-between gap-4 p-5 lg:p-6 text-left"
                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                    aria-expanded={openIndex === i}
                    aria-controls={`faq-panel-${i}`}
                  >
                    <span
                      className={`font-display text-base lg:text-lg font-semibold leading-snug transition-colors duration-200 ${
                        openIndex === i ? 'text-gold' : 'text-navy'
                      }`}
                    >
                      {faq.question}
                    </span>
                    <span
                      className={`flex-shrink-0 w-7 h-7 flex items-center justify-center border transition-all duration-200 mt-0.5 ${
                        openIndex === i
                          ? 'bg-gold border-gold text-white'
                          : 'border-navy/20 text-navy/60 hover:border-gold hover:text-gold'
                      }`}
                    >
                      {openIndex === i ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                    </span>
                  </button>

                  {/* Collapsed with CSS only — the text must stay in the HTML. */}
                  <div
                    id={`faq-panel-${i}`}
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === i ? 'max-h-[900rem] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-5 lg:px-6 pb-5 lg:pb-6 pt-0">
                      <div className="w-8 h-0.5 bg-gold mb-4" />
                      <p className="font-body text-navy/80 text-sm lg:text-base leading-relaxed mb-6">
                        {faq.answer}
                      </p>
                      <div className="border-t border-gold/15 pt-6">{renderBlocks(faq.blocks)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
